<template>
  <a-modal :title="`【${user.name}】的订单`"
           :open="modal"
           :width="1800"
           @cancel="cancelCk">
    <a-space class="nav-no-title">
      <a-select class="nav-selector"
                size="small"
                max-tag-count="responsive"
                :options="orderTypeList"
                v-model:value="pagination.type"
                placeholder="订单类型"
                @change="search"/>
      <a-select class="nav-selector"
                size="small"
                :options="orderStateList"
                v-model:value="pagination.state"
                @change="search"/>
      <a-select class="long-select"
                size="small"
                mode="multiple"
                max-tag-count="responsive"
                allowClear
                :options="itemList"
                v-model:value="pagination.item"
                placeholder="筛选商品"
                @change="search"/>
    </a-space>

    <a-table size="small"
             :columns="columns"
             :data-source="source"
             :pagination="pagination"
             :loading="loading"
             :onChange="handlePage">
      <template v-slot:bodyCell="{ column, record }">

        <template v-if="column.dataIndex === 'orderNo'">
          <div>内部：{{ record.orderNo }}</div>
          <div>支付：{{ record.outOrderNo }}</div>
          <div>商户：{{ record.merchantNo }}</div>
        </template>

        <template v-else-if="column.dataIndex === 'coupon'">
          <template v-if="record.couponList.length < 1">
            -
          </template>
          <a-popover v-else>
            <template #content>
              <a-list size="small" item-layout="vertical" bordered :data-source="record.couponList">
                <template #renderItem="{ item }">
                  <a-list-item>{{ item }}</a-list-item>
                </template>
              </a-list>
            </template>

            <a-tag :bordered="false" color="red" strong>{{ record.coupon }}</a-tag>
          </a-popover>
        </template>

        <template v-else-if="column.dataIndex === 'payPrice'">

          <a-tag v-if="record.payType === 0" :bordered="false" color="green">微信</a-tag>
          <a-tag v-else :bordered="false" color="blue">支付宝</a-tag>
          {{ record.payPrice }}
        </template>

        <template v-else-if="column.dataIndex === 'item'">
          <a-space>
            <template v-if="record.type === 0">
              <a-tag :bordered="false" color="green">普通</a-tag>
              <div v-for="(v, i) in record.item" :key="i">
                [{{ v.itemId }}] {{ v.itemName }} {{ v.branchName }} × {{ v.count }}
              </div>
            </template>
            <template v-else-if="record.type === 1">
              <a-tag :bordered="false" color="blue">抽卡</a-tag>
              <div v-for="(v, i) in record.item" :key="i">
                [{{ v.itemId }}] {{ v.itemName }} {{ v.branchName }} × {{ v.count }}
              </div>
            </template>
            <template v-else>
              <a-tag :bordered="false" color="orange">发货</a-tag>
              <a-typography-link @click="postViewCk(record)">发货列表</a-typography-link>
            </template>
          </a-space>
        </template>

        <template v-else-if="column.dataIndex === 'address'">
          <a-space direction="vertical">
            <div>{{ record.postName }} {{ record.postPhone }}</div>
            <div>{{ record.postArea }}</div>
            <div>{{ record.postAddress }}</div>
          </a-space>
        </template>

        <template v-else-if="column.dataIndex === 'postalNo'">
          <a-space direction="vertical">
            <div>{{ record.postCompany }}</div>
            <div>{{ record.postalNo }}</div>
          </a-space>
        </template>

        <template v-else-if="column.dataIndex === 'notes'">
          <a-space direction="vertical">
            <a-typography-text v-if="record.notes > 0" type="danger">有备注！</a-typography-text>
            <a-button @click="notesCk(record)" type="dashed" size="small">查看</a-button>
          </a-space>
        </template>

        <template v-else-if="column.dataIndex === 'state'">
          <a-space direction="vertical">
            <a-tag :bordered="false" :color="stateColor(record.state)">{{ record.stateText }}</a-tag>

            <a v-if="record.refundId > 0 && record.refundState > 0" @click="refundViewCk(record)">退款记录</a>
          </a-space>
        </template>

        <template v-else-if="column.dataIndex === 'time'">
          <div>创建：{{ record.createTime }}</div>
          <div>支付：{{ record.payTime }}</div>
          <div>发货：{{ record.postTime }}</div>
          <div>完成：{{ record.doneTime }}</div>
        </template>

        <template v-else>
          {{ record[column.dataIndex] }}
        </template>
      </template>
    </a-table>

    <template #footer></template>
  </a-modal>

  <upd-notes-modal ref="updNotes"/>

  <post-view-modal ref="postView"/>
</template>

<script setup>
import {ref} from 'vue'
import axios from '@/axios'

import UpdNotesModal from '@/views/order/UpdNotes.vue'
import PostViewModal from '@/views/order/PostView.vue'

const modal = ref(false)
const loading = ref(false)

const source = ref([])
const itemList = ref([])

const user = ref({})
const pagination = ref({
  showSizeChanger: true,
  showQuickJumper: true,
  defaultCurrent: 1,
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  type: -1,
  state: -9,
  item: [],
  total: 0,
  onShowSizeChange: (current, size) => {
    pagination.value.current = 1
    pagination.value.pageSize = size
    query()
  }
})

const updNotes = ref(null)
const postView = ref(null)

const columns = [
  {
    key: 'ID',
    title: 'ID',
    dataIndex: 'id',
  }, {
    key: 'orderNo',
    title: '订单号',
    dataIndex: 'orderNo',
    width: 300,
  }, {
    key: 'item',
    title: '内容',
    dataIndex: 'item',
    width: 300,
  }, {
    key: 'coupon',
    title: '折扣金额 [元]',
    dataIndex: 'coupon',
  }, {
    key: 'payPrice',
    title: '支付金额 [元]',
    dataIndex: 'payPrice',
  }, {
    key: 'address',
    title: '地址',
    dataIndex: 'address',
    width: 360,
  }, {
    key: 'postalNo',
    title: '快递单号',
    dataIndex: 'postalNo',
  }, {
    key: 'transportFee',
    title: '运费 [元]',
    dataIndex: 'transportFee',
  }, {
    key: 'notes',
    title: '备注',
    dataIndex: 'notes',
  }, {
    key: 'state',
    title: '状态',
    dataIndex: 'state',
  }, {
    key: 'time',
    title: '时间',
    dataIndex: 'time',
  }
]
const orderStateList = [
  {value: -9, label: '全部'},
  {value: -8, label: '待发货'},
  {value: 0, label: '待支付'},
  {value: 1, label: '已支付'},
  {value: 5, label: '即将发货'},
  {value: 2, label: '已发货'},
  {value: 3, label: '交易完成'},
  {value: -1, label: '过期'},
  {value: -2, label: '取消'},
  {value: 4, label: '交易关闭'},
]
const orderTypeList = [
  {value: -1, label: '全部'},
  {value: 0, label: '普通'},
  {value: 1, label: '抽卡'},
  {value: 2, label: '发货'},
  {value: 3, label: '退款'},
]

function queryItem() {
  axios.get('/order/export-item').then((res) => {
    if (res.code === 1) {
      itemList.value = res.data
    }
  })
}

function query() {
  loading.value = true

  axios.get('/order', {
    params: {
      page: pagination.value.current,
      size: pagination.value.pageSize,
      type: pagination.value.type,
      state: pagination.value.state,
      userId: user.value.id,
      item: pagination.value.item,
      search: '',
    }
  }).then((res) => {
    loading.value = false

    if (res.code === 1) {
      pagination.value.current = res.data.page
      pagination.value.total = res.data.total
      source.value = res.data.data
    }
  })
}

function search() {
  pagination.value.current = 1
  query()
}

function stateColor(state) {
  if (state === -2 || state === -1 || state === 5) {
    return 'red'
  }
  if (state === 0) {
    return 'orange'
  }
  if (state === 2) {
    return 'processing'
  }
  return 'green'
}

function notesCk(record) {
  updNotes.value.openCk(record)
}

function handlePage(param) {
  pagination.value.current = param.current
  pagination.value.pageSize = param.pageSize
  query()
}

function openCk(record) {
  user.value = record
  pagination.value.current = 1
  query()
  queryItem()
  modal.value = true
}

function cancelCk() {
  modal.value = false
}

function postViewCk(record) {
  postView.value.openCk(record)
}

defineExpose({openCk})
</script>

<style scoped>
.long-select {
  width: 300px;
}
</style>